import { Grid } from '@mui/material';
import { FormikErrors, FormikTouched, useFormikContext } from 'formik';
import {
  AuthorizationDataVM,
  AuthorizationVoteFormVM,
} from 'front-office/view-models/authorization-vote';
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import AccordionPerson from 'shell/components/shared/person';
import AccordionElectoralData from './electoral-card-data';
import PrintAuthorizationVoteButtons from './buttons';
import AccordionAuthorizationData from './authorization-data';
import SearchMessages, { MessageType } from 'shell/components/shared/search-messages';
import { ElectoralErrorHandler } from 'shell/components/shared/electoral-error-modal';
import AnomalieErrorModal from 'shell/components/shared/anomalie-modal';
import { Anomaly } from 'front-office/view-models/common-certificate-form';
import { useNavigate } from 'react-router-dom';

interface PrintAuthorizationVoteFormProps {
  onExit: () => void;
  getElectoralPersonData: () => void;
}

const PrintAuthorizationVoteForm = ({
  onExit,
  getElectoralPersonData,
}: PrintAuthorizationVoteFormProps): React.ReactElement => {
  const formik = useFormikContext<AuthorizationVoteFormVM>();

  const navigate = useNavigate();

  const [modalBlockingError, setModalBlockingError] = useState({ onConfirm: false, open: true });
  const [modalNonBlockingError, setModalNonBlockingError] = useState(false);
  const [errorModalOpen, setErrorModal] = useState<boolean>(false);

  const [modalErrorElectoral, setModalErrorElectoral] = useState({ open: false, message: '' });

  const onFieldAuthorizationDataChange = useCallback(
    (key, value) => {
      formik.setFieldValue('authorizationData', {
        ...(formik.values.authorizationData || {}),
        [key]: value,
      });
    },
    [formik.setFieldValue, formik.values.authorizationData]
  );

  const onFieldsAuthorizationDataChange = useCallback(
    (values) => {
      formik.setFieldValue('authorizationData', {
        ...(formik.values.authorizationData || {}),
        ...values,
      });
    },
    [formik.setFieldValue, formik.values.authorizationData]
  );

  useEffect(() => {
    if (formik.values.electoralData?.blockError) {
      setModalBlockingError({ open: true, onConfirm: false });
    }
    if (formik.values.electoralData?.nonBlockError) {
      setModalNonBlockingError(true);
    }
  }, [formik.values.electoralData?.blockError, formik.values.electoralData?.nonBlockError]);

  const anomalieList: Array<Anomaly> = useMemo(() => {
    return formik.values.electoralData?.anomalyList || [];
  }, [formik.values.electoralData?.anomalyList]);

  useEffect(() => {
    if (anomalieList?.length > 0) {
      setErrorModal(true);
    }
  }, [anomalieList]);

  // useEffect(() => {
  //   if (
  //     formik.values.electoralData?.indicatoreListaC === '1' ||
  //     formik.values.electoralData?.indicatoreListaE === '1'
  //   ) {
  //     setModalErrorElectoral({
  //       open: true,
  //       message: 'Elettore solo Comunali o Europee - utilizzare tessera Mod.73',
  //     });
  //   }
  // }, [
  //   formik.values.electoralData?.indicatoreListaC,
  //   formik.values.electoralData?.indicatoreListaE,
  // ]);

  return (
    <Fragment>
      <SearchMessages
        type={MessageType.Warning}
        title={"C'è un warning"}
        messages={[{ id: 1, value: formik.values.electoralData?.nonBlockError || '' }]}
        visibility={modalNonBlockingError}
        onChangeVisibility={() => setModalNonBlockingError(false)}
      />
      {(anomalieList as Array<Anomaly>)?.length > 0 ? (
        <AnomalieErrorModal
          open={errorModalOpen}
          onClose={() => {
            setErrorModal(false);
          }}
          dataPersons={formik.values.electoralData?.anomalyList as Array<Anomaly>}
        />
      ) : undefined}
      {formik.values.electoralData?.blockError ? (
        <ElectoralErrorHandler
          open={modalBlockingError.open}
          onClose={() => {
            if (!modalBlockingError.onConfirm) {
              navigate('/sportello/autorizzazione-voto');
            }
            setModalBlockingError({ open: false, onConfirm: false });
          }}
          message={formik.values.electoralData?.blockError}
          title="Errore Bloccante"
        />
      ) : undefined}
      {modalErrorElectoral.open ? (
        <ElectoralErrorHandler
          open={modalErrorElectoral.open}
          onClose={() => {
            setModalErrorElectoral({ open: false, message: '' });
          }}
          message={modalErrorElectoral.message}
          title="Warning"
        />
      ) : undefined}
      <Grid container spacing={2}>
        <Grid item xs={12} md={3}>
          <PrintAuthorizationVoteButtons
            onExit={onExit}
            getElectoralPersonData={getElectoralPersonData}
          />
        </Grid>
        <Grid item xs={12} md={9}>
          <AccordionPerson disabled />
          <AccordionElectoralData values={formik.values?.electoralData} />
          <AccordionAuthorizationData
            onChange={onFieldAuthorizationDataChange}
            onChangeValues={onFieldsAuthorizationDataChange}
            values={formik.values?.authorizationData}
            errors={formik.errors?.authorizationData as FormikErrors<AuthorizationDataVM>}
            touched={
              formik.touched?.authorizationData as unknown as FormikTouched<AuthorizationDataVM>
            }
            disabled={!formik.values?.isAuthorized}
          />
        </Grid>
      </Grid>
    </Fragment>
  );
};

PrintAuthorizationVoteForm.displayName = 'Print authorization Vote form component';

export default PrintAuthorizationVoteForm;
